import type { Districts } from "@reducers/actions/apiActions/interface";
import { dispatchSetMultipleUI } from "@reducers/uiReducer";
import StorageUtils from "@services/storage";
import type { Tables } from "@services/storage/model";
import type { TemplateUIObject } from "@services/storage/storeTemplate";
import CancellationTokenStore from "@services/tokens";
import dayjs from "dayjs";
import {
	isValid,
	toArea,
	toDistrict,
	toIncode,
	toNormalised,
	toOutcode,
	toSector,
	toSubDistrict,
	toUnit,
} from "postcode";
import { Address, type AddressRecord } from "uk-clear-addressing";
import { cacheUtils } from "../..";
import { state } from "../../interfaces/interfaces";
import type { AddressPrefix } from "../BasicTypes";
import type { CacheTypes } from "../cache/interfaces";
import postcodeToCountyMapper from "../postcodeToCountyMapper";
import { StringProcessor } from "../stringProcessor";
import type {
	AddressFormFields,
	AddressFromAPI,
	CheckAddressBlacklist,
	CheckAddressBlacklistForCaravanSites,
	CheckCoverage,
	ClearPropertyAddressForm,
	ConvertAddressFormFieldsToAddress,
	ConvertToClientScope,
	ConvertToIndexedDBScope,
	FixPropertyInOldAddress,
	FormatPostCode,
	GetPostcodeArea,
	GetPostcodeDistrict,
	GetPostcodeSector,
	GetPostcodeValues,
	GetProblemInformationAddressString,
	HandlePostCodeChange,
	LookupBookingRestriction,
	LookupResponseTime,
	MapAddressToFormAddress,
	MergedAddresses,
	ProcessAddressesFromDB,
	Scope,
	SearchAddresses,
	SearchAddressesProblem,
	ShowAddressModal,
	ValidatePostCode,
} from "./model"; // Import the model interface
import { PremiseExtractor } from "./PremiseExtractor";

type temp =
	| "Property"
	| "Property"
	| "Street"
	| "City"
	| "Locality"
	| "Postcode"
	| "BusinessName";
// ----------------------------------------------------------------------------
// Author:  Adam Rzymski
// Project: Booking System
// Name:    AddressUtils
// Date:    26/03/2022
// Version: 1.0.0
// Type:    Class
// Purpose: Provides a class for processing addresses and triggers UI actions
// ----------------------------------------------------------------------------
//
// Language: typescript
// Path: ClientApp\src\utils\address\index.ts
//
// ----------------------------------------------------------------------------
// Description:
// This class provides various static utility methods for processing address,
// and triggering UI actions. To use simply import AddressUtils from this file.
// Because this class is a static class, it is not required to instantiate it.
// Methods use interfaces to define the input and output parameters.
// Interfaces are defined in model/index.ts
// Some of the methods are async, and therefore return promises.
// Some of the async methods are using the API, and also return promises.
// Methods which may compute a result, sometimes modify instances of the
// other objects passed to them. These objects are passed by reference, and are
// therefore modified by the method. This is to modify application state.
// ----------------------------------------------------------------------------
// Imports:
// ----------------------------------------------------------------------------
// ----------------------------------------------------------------------------
// Class
// ----------------------------------------------------------------------------
/**
 * A a class for processing addresses and triggers UI actions
 * @class AddressUtils
 * @remarks This class provides various static utility methods for processing address,
 * and triggering UI actions. To use simply import AddressUtils from this file.
 * Because this class is a static class, it is not required to instantiate it.
 * Methods use interfaces to define the input and output parameters.
 * Interfaces are defined in model/index.ts
 * Some of the methods are async, and therefore return promises.
 * Some of the async methods are using the API, and also return promises.
 * Methods which may compute a result, sometimes modify instances of the
 * other objects passed to them. These objects are passed by reference, and are
 * therefore modified by the method. This is to modify application state.
 * @constructor
 * @author Adam Rzymski
 * @date 2020-03-29
 * @version 1.0.0
 * @example
 * import AddressUtils from '@app/utilities/address';
 * @property nameSpace {string} - The namespace for the class
 * @method addressFormFields {AddressFormFields} - The address form fields
 * @method addressToAddressString {AddressToAddressString} - The address to address string
 * @method checkAddressBlacklist {CheckAddressBlacklist} - The check address blacklist
 * @method checkCoverage {CheckCoverage} - The check coverage
 * @method clearPropertyAddressForm {ClearPropertyAddressForm} - The clear property address form
 * @method convertAddressFormFieldsToAddress {ConvertAddressFormFieldsToAddress} - The convert address form fields to address
 * @method fixPropertyInOldAddress {FixPropertyInOldAddress} - The fix flat in old address
 * @method formatPostCode {FormatPostCode} - The format post code
 * @method getPostcodeDistrict {GetPostcodeDistrict} - The get postcode district
 * @method getPostcodeArea {GetPostcodeArea} - The get postcode area
 * @method getPostcodeSector {GetPostcodeSector} - The get postcode sector
 * @method getPostcodeValues {GetPostcodeValues} - The get postcode values
 * @method handlePostCodeChange {HandlePostCodeChange} - The handle post code change
 * @method lookupBookingRestriction {LookupBookingRestriction} - The lookup booking restriction
 * @method lookupResponseTime {LookupResponseTime} - The lookup response time
 * @method mapAddressToFormAddress {MapAddressToFormAddress} - The map address to form address
 * @method mergedAddresses {MergedAddresses} - The merged addresses
 * @method processAddressesFromDB {ProcessAddressesFromDB} - The process addresses from db
 * @method searchAddresses {SearchAddresses} - The search addresses
 * @method searchAddressesProblem {SearchAddressesProblem} - The search addresses problem
 * @method showAddressModal {ShowAddressModal} - The show address modal
 * @method validatePostCode {ValidatePostCode} - The validate post code
 * @public
 * @returns {AddressUtils}
 */

export class AddressUtils {
	public static cancellationTokens: CancellationTokenStore =
		new CancellationTokenStore();
	public static nameSpace: string = "AddressUtils";

	/**
	 * Validates the postcode and returns the boolean value of the validation
	 * @method validatePostCode
	 * @param postcode {string} - The postcode to validate
	 * @returns {boolean} - The validation result
	 * @public
	 * @static
	 * @example
	 * AddressUtils.validatePostCode('AB1 2CD');
	 */
	public static validatePostcode: ValidatePostCode = (postcode) => {
		// return the result of the match
		return isValid(postcode);
	};

	/**
	 * Formats the postcode to the correct format and returns the formatted postcode
	 * @method formatPostCode
	 * @param postcode {string} - The postcode to format
	 * @returns {string} - The formatted postcode
	 * @public
	 * @static
	 * @example
	 * AddressUtils.formatPostCode('AB1 2CD');
	 * // returns 'AB1 2CD'
	 */

	public static formatPostcode: FormatPostCode = (postcode) => {
		if (isValid(postcode)) {
			return toNormalised(postcode) as string;
		}
		// if the postcode is not valid, return the postcode as is
		return postcode;
	};

	// ConvertAddressFormFieldsToAddress:
	// --------------------------------------------------------------------------
	// Converts the address fields from address form back to Address object
	// --------------------------------------------------------------------------
	/**
	 * Converts the address fields from address form Address object
	 * @method convertAddressFormFieldsToAddress
	 * @param addressFormFields {AddressFormFields} - The address form fields
	 * @public
	 * @static
	 * @example
	 * AddressUtils.convertAddressFormFieldsToAddress(addressFormFields);
	 * // returns address
	 * @remarks
	 * This method is used to convert the address form fields to an address object.
	 * @example
	 * const addressFormFields: AddressFormFields = {
	 *  BushinessName: '',
	 * Property: '',
	 * Property: '',
	 * Postcode: '',
	 * Street: '',
	 * City: '',
	 * County: '',
	 * Country: '',
	 * };
	 * const address: Address = AddressUtils.convertAddressFormFieldsToAddress(addressFormFields);
	 * @returns {Address} - The address
	 * address: Address - The address
	 */

	public static convertAddressFormFieldsToAddress: ConvertAddressFormFieldsToAddress =
		({ ui, scope }) => {
			// Can check if the address is changed at all from the onw that is selected in the dropdowns.
			const matchingAddress = AddressUtils.findMatchingAddressFromDropdowns(
				ui,
				scope,
			);
			// create the Address Form Fields
			const addressFormFields: AddressFormFields = {};
			// array of address form fields that are required
			const requiredAddressFields = [
				"Property",
				"Street",
				"City",
				"Locality",
				"Postcode",
				"BusinessName",
			];

			// loop through the required address fields
			for (const fieldName of requiredAddressFields) {
				const fieldValue = ui[`${scope}${fieldName as temp}`];
				// if the field is not empty
				if (fieldValue) {
					addressFormFields[fieldName as keyof AddressFormFields] = fieldValue;
				}
			}
			// if the address form street is empty return null
			if (!addressFormFields?.Street) return null;

			// destruct the address form fields into the address fields
			const {
				BusinessName,
				Property,
				Street,
				Locality,
				City,
				County,
				Postcode,
			} = addressFormFields;

			// Function to process the address and create the pafRecord
			const createPafRecord = (
				Property: string,
				Postcode: string,
				City: string,
				Street: string,
				BusinessName?: string,
				Locality?: string,
				County?: string,
			) => {
				const revertedPremise = PremiseExtractor.revertPremise(Property, {
					thoroughfare: Street,
					organisation_name: BusinessName,
					department_name: "",
				});

				console.log("revertedPremise", revertedPremise);

				// Initialize values
				let subBuilding: string | undefined;
				let buildingNumber: string | undefined;

				// if (match) {
				//   subBuilding = match[1] ? match[1].trim() : undefined; // Capture sub-building if available
				//   buildingName = match[2] ? match[2].trim() : undefined; // Capture building name if available
				//   buildingNumber = match[3].trim(); // Capture building number or the last component

				//   // Check if the final captured group is purely numeric or alphanumeric
				//   if (/^\d+[a-zA-Z]?$/.test(buildingNumber)) {
				//     // It's a building number, so keep `buildingNumber` as is
				//   } else {
				//     // If the last segment is not numeric, treat it as part of the building name
				//     if (buildingName) {
				//       buildingName = `${buildingName}, ${buildingNumber}`; // Merge with the existing building name
				//     } else {
				//       buildingName = buildingNumber; // If no existing building name, use this part
				//     }
				//     buildingNumber = undefined; // Clear buildingNumber since it's now part of buildingName
				//   }
				// } else {
				// If no match, treat the entire Property as the building name
				const buildingName = Property.trim();
				// }

				// console.log("subBuilding", subBuilding);
				// console.log("buildingName", buildingName);
				// console.log("buildingNumber", buildingNumber);

				// Construct the pafRecord
				const pafRecord: AddressRecord = {
					postcode: Postcode || undefined,
					post_town: City || undefined,
					thoroughfare: Street || undefined,
					building_name: buildingName || undefined,
					building_number: buildingNumber || undefined,
					sub_building_name: subBuilding || undefined,
					organisation_name: BusinessName || undefined,
					dependant_locality: Locality || undefined,
					county: County || undefined,
				};

				return pafRecord;
			};

			const {
				line_1,
				line_2,
				line_3,
				premise,
				number,
				unit,
				post_town,
				postcode,
			} = matchingAddress
				? new Address({
						...Object.fromEntries(
							Object.entries(matchingAddress?.addressObj ?? {}).map(
								([key, value]) => [key, value === null ? undefined : value],
							),
						),
					})
				: new Address(
						createPafRecord(
							Property || "",
							Postcode || "",
							City || "",
							Street || "",
							BusinessName || "",
							Locality || "",
							County || "",
						),
					);

			// create empty address object
			const address: AddressFromAPI = matchingAddress
				? {
						addressString: matchingAddress?.addressString,
						organisationName: matchingAddress?.organisationName,
						isBusiness: matchingAddress?.isBusiness ?? false,
						street: matchingAddress?.street,
						locality: matchingAddress?.locality,
						city: matchingAddress?.city,
						county: matchingAddress?.county,
						postcode: matchingAddress?.postcode,
						id: matchingAddress?.id,
						area: matchingAddress?.area,
						district: matchingAddress?.district,
						sector: matchingAddress?.sector,
						addressObj: {
							...matchingAddress?.addressObj,
						},
					}
				: {
						addressString: "",
						organisationName: BusinessName,
						isBusiness: !!BusinessName,
						street: Street,
						locality: Locality,
						city: City,
						county: County,
						postcode: Postcode ?? "",
						id: 0,
						area: toArea(Postcode ?? "") || "",
						district: toDistrict(Postcode ?? "") || "",
						sector: toSector(Postcode ?? "") || "",
						addressObj: {
							line1: line_1,
							line2: line_2,
							line3: line_3,
							premise: premise,
							number: number,
							unit: unit,
							postTown: post_town,
							postcode: postcode,
						},
					};

			const returnAddress: Partial<AddressFromAPI> & { scope?: string } =
				matchingAddress ? { ...matchingAddress } : {};

			// loop through the address object and add the values to the return address object
			// if the value is not empty
			if (!matchingAddress)
				for (const fieldName in address) {
					const fieldValue = address[fieldName as keyof typeof address];
					// if the field is not empty
					if (fieldValue) {
						// set the return address object to the trimmed address object value
						(returnAddress as any)[
							fieldName as keyof Partial<AddressFromAPI> & {
								scope?: string | undefined;
							}
						] = String(fieldValue).trim();
					}
					if (fieldName === "postcode" && fieldValue) {
						// map the county to the address object
						const district = toDistrict(fieldValue as string);
						returnAddress.county = postcodeToCountyMapper(
							district as Districts,
						);
					}
					if (fieldName === "addressObj") {
						// map the county to the address object
						returnAddress.addressObj = {
							...address.addressObj,
						};
					}
				}
			// if the return address object is not empty
			if (Object.keys(addressFormFields).length > 0) {
				// if no address string is set
				if (!returnAddress.addressString) {
					// create the address string from the return address object and add it to the return address object
					returnAddress.addressString = `${returnAddress.organisationName ? `${returnAddress.organisationName}, ` : ""}${returnAddress?.addressObj?.premise} ${returnAddress.street}, ${returnAddress.locality ? `${returnAddress.locality}, ` : ""}${returnAddress.city}, ${returnAddress.county ? `${returnAddress.county ? `${returnAddress.county}, ` : ""}` : ""}${returnAddress.postcode}`;
				}

				// add scope to the return address object
				returnAddress.scope =
					`Stored: ${StringProcessor.camelToWords(scope)}`.replace(
						"Care Plan Plan Details",
						"Care Plan Membership",
					) as Scope;
			}

			// return the return address object
			return returnAddress as AddressFromAPI | null;
		};

	/**
	 * Merges the addresses and returns the merged addresses array.
	 * @remarks - Combines addresses which are already stored for other forms in the booking,
	 * with the new address resulting from searching for the address.
	 * Puts existing address in the first position of the array.
	 * If existing addresses replicates any of the other accounts addresses,
	 * the existing address is removed from the array and accounts are merged.
	 * Existing addresses indicate the type of the account related to. ie. Landlord, Tenant, etc.
	 * @method mergeAddresses
	 * @param {{ temp: object, ui: object, prefix: string }} - The fields
	 * @public
	 * @static
	 * @returns {Address[]} - The merged addresses
	 * @example
	 * AddressUtils.mergeAddresses({ temp, ui, prefix });
	 * // returns merged addresses
	 */
	public static mergeAddresses: MergedAddresses = async ({
		temp,
		ui,
		prefix,
	}) => {
		// create existing addresses array
		const existingAddresses: AddressFromAPI[] = [];

		// create current search addresses array
		// map the current search addresses and add scope as "Search"
		const cachedAddresses =
			await StorageUtils.getAllValuesFromStoreInIndexedDB<AddressFromAPI>({
				tableName: `${this.convertToIndexedDBScope(prefix)}Addresses` as Tables,
			});
		const currentSearchAddresses = cachedAddresses?.map((a) => ({
			...a,
			scope: "Search" as Scope,
		})) as AddressFromAPI[];

		// iterate through the accountsList, if tenant lives in property and current account is "Landlord", skip the account
		for (const account of temp.AddressAccountsListAllowedToMerge) {
			if (!state[`${account}Valid`]) {
				continue;
			}
			// otherwise push the account address to the temp address array if not empty
			// create the address object for account
			const accountAddress = this.convertAddressFormFieldsToAddress({
				ui,
				scope: account,
			});
			// if the account address is not empty, push it to the existing addresses array
			if (accountAddress) {
				// check if the account address is already in the existing addresses array
				const existingAddress = existingAddresses.find(
					(a) => a.addressString === accountAddress.addressString,
				);
				// if the account address is in the existing addresses array,
				if (existingAddress) {
					// merge the scope of the account address with the existing address
					// replace existing address in the existing addresses array with the account address and remove "Stored: " word from the scope
					existingAddresses[existingAddresses.indexOf(existingAddress)] = {
						...existingAddress,
						scope:
							`${existingAddress.scope}, ${accountAddress.scope?.replace("Stored: ", "")}` as Scope,
					};
				} else {
					// otherwise, the account address is not in the existing addresses array, push it to the existing addresses array
					existingAddresses.push(accountAddress);
				}
			}
		}

		// create the merged addresses array using the current addresses and temp addresses with spread operator
		const mergedAddresses = [...existingAddresses, ...currentSearchAddresses];

		// update addresses length for prefix on state store
		temp[`${prefix}AddressesLength`] = mergedAddresses.length;

		// return the return address array
		return mergedAddresses;
	};
	// ### END: MergeAddresses

	// ShowAddressModal:
	// --------------------------------------------------------------------------
	// Shows the address modal by setting ui modal to true
	// --------------------------------------------------------------------------
	public static showAddressModal: ShowAddressModal = ({ ui, prefix }) => {
		console.log("showAddressModal", prefix);
		if (prefix !== "PropertyAddress") {
			ui[`${prefix}ModalOpen`] = true;
		}
	};
	// ### END: ShowAddressModal

	// SearchAddresses:
	// --------------------------------------------------------------------------
	// Searches the addresses and returns the searched addresses array
	// --------------------------------------------------------------------------
	public static searchAddresses: SearchAddresses = async ({
		temp,
		api,
		ui,
		prefix,
	}) => {
		if (temp[`${prefix}ActiveSearch`])
			try {
				await api.addressSearch({
					postcode: ui[`${prefix}Postcode`] || "",
					scope: prefix,
				});
			} catch (error) {
				console.error(error);
			}
	};
	// ### END: SearchAddresses

	// FixPropertyInOldAddress:
	// --------------------------------------------------------------------------
	// Fixes the typed flat name in the old address format stored currently in DB
	// --------------------------------------------------------------------------
	public static fixPropertyInOldAddress: FixPropertyInOldAddress = (
		property,
	) => {
		// regex to get property and flat from single string
		const regex =
			/^(?<flat>flat\s?)(?<number>\d{1,4}),?\s?(?<property>[\dA-z ]+)$/i;
		const match = regex.exec(property);
		if (match) {
			const flat = match?.groups?.flat;
			const number = match?.groups?.number;
			const property = match?.groups?.property;
			// regex last comma from string
			const cleanOut = /,$/g;
			// regex to remove space in front of comma
			const cleanOut2 = /\s+,/g;
			// regex named spacesFix to replace multiple spaces with single space
			const spacesFix = /\s+/g;
			return `${flat ? `${flat.charAt(0).toUpperCase()}${flat.substring(1)}${number ? ` ${number}` : ""}, ` : ""}${
				property ? ` ${property}` : ""
			}`
				.replaceAll(cleanOut2, ",") // remove space before comma
				.replaceAll(spacesFix, " ") // replace multiple spaces with single space
				.trim() // trim whitespace
				.replace(cleanOut, "") // remove last comma
				.trim(); // trim whitespace
		}
		return property?.trim();
	};
	// ### END: FixPropertyInOldAddress

	// GetPostcodeValues:
	// --------------------------------------------------------------------------
	// Gets the district, area and sector from the postcode and returns the values
	// Sets the area, district and sector values on the job object
	// --------------------------------------------------------------------------
	public static getPostcodeValues: GetPostcodeValues = ({
		postcode,
		scope,
	}) => {
		// use regex to get postcode area
		// const postcodeArea = postcode.match(
		//   /^(?<unit>(?<sector>(?<district>(?<area>[A-Z][A-Z]{0,1})[0-9][A-Z0-9]{0,1}) {0,}[0-9])[A-Z]{2})$/
		// );
		const normalised = toNormalised(postcode); // => postcode
		const outcode = toOutcode(postcode); // => "SW1A"
		const incode = toIncode(postcode); // => "2AA"
		const area = toArea(postcode); // => "SW"
		const district = toDistrict(postcode); // => "SW1"
		const subDistrict = toSubDistrict(postcode); // => "SW1A"
		const sector = toSector(postcode); // => "SW1A 2"
		const unit = toUnit(postcode); // => "AA
		if (scope === "Property") {
			const postcodeAreas =
				cacheUtils.getCache<CacheTypes.PostcodeAreas>("PostcodeAreas");
			const areaNumber = postcodeAreas.find(
				(a) => a.postcodeArea === area,
			)?.areaNumber;
			state.JobPostcodeArea = area || "";
			state.JobPostcodeDistrict = district || "";
			state.JobPostcodeSector = sector || "";
			state.JobAreaNumber = areaNumber || 0;
		}
		// return the postcode area
		return {
			postcode,
			normalised,
			outcode,
			incode,
			area,
			district,
			subDistrict,
			sector,
			unit,
		};
	};
	// ### END: GetPostcodeValues

	// GetPostcodeArea:
	// --------------------------------------------------------------------------
	// Gets the postcode area from the postcode and returns the area, also sets the postcode area in the job
	// --------------------------------------------------------------------------
	public static getPostcodeArea: GetPostcodeArea = ({ postcode }) => {
		// use regex to get postcode area
		const postcodeArea = this.getPostcodeValues({ postcode });
		// return the postcode area
		return postcodeArea?.area || "";
	};
	// ### END: GetPostcodeArea

	// GetPostcodeDistrict:
	// --------------------------------------------------------------------------
	// Gets the postcode district from the postcode and returns the district, also sets the postcode district in the job
	// --------------------------------------------------------------------------
	public static getPostcodeDistrict: GetPostcodeDistrict = ({ postcode }) => {
		// use regex to get postcode area
		const postcodeArea = this.getPostcodeValues({ postcode });
		// return the postcode area
		return postcodeArea?.district;
	};
	// ### END: GetPostcodeDistrict

	// GetPostcodeSector:
	// --------------------------------------------------------------------------
	// Gets the postcode sector from the postcode and returns the sector, also sets the postcode sector in the job
	// --------------------------------------------------------------------------
	public static getPostcodeSector: GetPostcodeSector = ({ postcode }) => {
		// use regex to get postcode area
		const postcodeArea = this.getPostcodeValues({ postcode });
		// return the postcode area
		return postcodeArea?.sector;
	};
	// ### END: GetPostcodeSector

	// HandlePostCodeChange:
	// --------------------------------------------------------------------------
	// Handles the postcode change by validating the postcode and formatting it
	// This is used in all address forms
	// Sets the ActiveSearch to valid if the postcode is valid
	// Sets the ui postcode to the formatted postcode
	// --------------------------------------------------------------------------
	public static handlePostCodeChange: HandlePostCodeChange = ({
		event,
		ui,
		temp,
		prefix,
	}) => {
		let value = event.target.value;
		const name = event.target.name as "Postcode";
		// if postcode is valid, set the postcode to the formatted postcode
		if (this.validatePostcode(value)) value = this.formatPostcode(value);

		// set the postcode in the ui
		ui[`${prefix}${name}`] = value as never;

		// if postcode is valid, set the active search to valid
		temp[`${prefix}ActiveSearch`] = this.validatePostcode(value) as never;
	};
	// ### END: HandlePostCodeChange

	// SearchAddressesProblem:
	// --------------------------------------------------------------------------
	// Searches the addresses and returns the searched addresses array
	// This is used in initial address search form
	// --------------------------------------------------------------------------
	public static searchAddressesProblem: SearchAddressesProblem = async ({
		temp,
		api,
		ui,
	}) => {
		// if the search is active, search the addresses
		if (temp.ProblemAddressActiveSearch) {
			// reset temp values for the search if they exist
			temp.ProblemResponseTimes = [];
			temp.PropertyAddressAddresses = [];
			temp.ProblemAddressCoverage = true;
			temp.ProblemAddressPostcodeValid = false;
			temp.ProblemAddressAreaCode = "";
			temp.ProblemAddressNewAreaCode = "";
			ui.ProblemAddressString = "";

			// fetch the addresses from the api for the postcode
			await api.addressSearch({
				postcode: ui.ProblemInputPostcode,
				scope: "PropertyAddress",
			});
		}
	};
	// ### END: SearchAddressesProblem

	// CheckAddressBlacklist:
	// --------------------------------------------------------------------------
	// Checks the address against the address blacklist
	// Returns true if the address is blacklisted
	// --------------------------------------------------------------------------
	public static checkAddressBlacklist: CheckAddressBlacklist = async ({
		ui,
		address,
	}) => {
		// create blacklist flag
		let blacklist = false;
		let result: CacheTypes.AddressBlacklist | undefined;
		// get the address blacklist
		const addressBlacklist =
			cacheUtils.getCache<CacheTypes.AddressBlacklist>("AddressBlacklist");
		// if address exists, check against blacklist
		if (address) {
			result = addressBlacklist?.find(
				(x) =>
					x.postcode === state.JobPostcode &&
					x.property?.substring(0, 10) === address,
			);
		}
		// if job is set, check the address against the blacklist
		else if (state.JobPostcode && state.JobProperty) {
			// check the address against the blacklist
			result = addressBlacklist?.find(
				(x) =>
					x.postcode === state.JobPostcode && x.property === state.JobProperty,
			);
		}

		if (result && result.reason !== "Caravan Site") {
			// if the address is blacklisted, set the blacklist flag and display the reason message
			blacklist = true;
			ui.ProblemMsg =
				"This address is blocked. \nPlease refer to your manager before booking this customer.";
			ui.ProblemMsgVariant = "error";
			ui.ProblemShowMsg = true;
		}
		// return the blacklist flag
		return blacklist;
	};
	// ### END: CheckAddressBlacklistForCaravanSites
	// CheckAddressBlacklistForCaravanSites:
	// --------------------------------------------------------------------------
	// Checks the address against the address blacklist
	// Returns true if the address is blacklisted ex "WR11 5SA"
	// --------------------------------------------------------------------------
	public static checkAddressBlacklistForCaravanSites: CheckAddressBlacklistForCaravanSites =
		async ({ ui, address: _address }) => {
			// create blacklist flag
			let blacklist = false;
			let result: CacheTypes.AddressBlacklist | undefined;
			// get the address blacklist
			const addressBlacklist =
				cacheUtils.getCache<CacheTypes.AddressBlacklist>("AddressBlacklist");
			// if address exists, check against blacklist
			if (state.JobPostcode) {
				// check the address against the blacklist, for caravan parks
				result = addressBlacklist?.find(
					(x) =>
						x.postcode === state.JobPostcode && x.reason === "Caravan Site",
				);

				if (result) {
					ui.ProblemMsg =
						"Please verify with the customer that this is a caravan park. If so, please fail the job as we do not cover caravan parks.";
					ui.ProblemMsgVariant = "warning";
					ui.ProblemShowMsg = true;
					blacklist = true;
				}
			}
			// return the blacklist flag
			return blacklist;
		};
	// ### END: CheckAddressBlacklistForCaravanSites

	// LookupBookingRestriction:
	// --------------------------------------------------------------------------
	// Looks up the booking restriction for the address
	// Returns the booking restriction
	// --------------------------------------------------------------------------
	public static lookupBookingRestriction: LookupBookingRestriction = async ({
		ui,
		token,
		callbackFn,
	}) => {
		const bookingRestrictions =
			cacheUtils.getCache<CacheTypes.BookingRestrictions>(
				"BookingRestrictions",
			);

		// check the booking restriction for match
		const bookingRestriction = bookingRestrictions?.find(
			(x) =>
				x.postcodeArea.trim() === state.JobPostcodeArea &&
				x.tradeCode.trim() === state.JobTradeCode,
		);

		state.RestrictionExpiresIn30Days = -1;

		// if the booking restriction is found and the booking restriction next booking date is set and is in the future, set the booking restriction next booking date
		if (
			bookingRestriction?.jobBookingRestrictionId &&
			bookingRestriction?.dateNextAvailable &&
			new Date(bookingRestriction?.dateNextAvailable) > new Date()
		) {
			// set the booking restriction next booking date
			const nextDateString = bookingRestriction?.dateNextAvailable
				? new Date(bookingRestriction?.dateNextAvailable).toLocaleDateString(
						"en-GB",
					)
				: "";

			// set the coverage message
			if (!token.isCancelled) {
				ui.JobCoverage = {
					...ui.JobCoverage,
					msg: `Booking restrictions apply until ${nextDateString}.`,
					variant: "warning",
				};

				// set snackbar message
				ui.ProblemMsg = `Booking restrictions apply until ${nextDateString}.`;
				ui.ProblemMsgVariant = "warning";

				// if callType id is 2, set different message
				if (bookingRestriction.callTypeId === 2) {
					// set the coverage message
					ui.JobCoverage = {
						...ui.JobCoverage,
						msg: `Booking restrictions apply until ${nextDateString}.`,
					};
					// set snackbar message
					ui.ProblemMsg = `There is a quote job type and area restriction in place.\nPlease fail the booking as 'No Coverage'.`;
				}
			}
			// return true indicating the booking restriction is in place
			if (!token.isCancelled && callbackFn) callbackFn(false);

			// set flat if restriction expires in 30 days
			// create a new date and add 30 days
			const dateIn30Days = dayjs().add(30, "day");

			const dateToday = dayjs();

			const dateNextAvailable = dayjs(bookingRestriction?.dateNextAvailable);

			if (
				dateNextAvailable.isAfter(dateToday) &&
				dateNextAvailable.isBefore(dateIn30Days)
			) {
				// set flag to difference in number of days
				state.RestrictionExpiresIn30Days =
					dateNextAvailable.diff(dateToday, "day") + 1;

				// set the coverage message
				if (!token.isCancelled) {
					ui.JobCoverage = {
						...ui.JobCoverage,
						msg: `Booking restrictions apply until ${nextDateString}. You can book this job in ${state.RestrictionExpiresIn30Days} days.`,
						variant: "warning",
					};

					// set snackbar message
					ui.ProblemMsg = `Booking restrictions apply until ${nextDateString}. You can book this job in ${state.RestrictionExpiresIn30Days} days.`;
					ui.ProblemMsgVariant = "warning";

					// if callType id is 2, set different message
					if (bookingRestriction.callTypeId === 2) {
						// set the coverage message
						ui.JobCoverage = {
							...ui.JobCoverage,
							msg: `Booking restrictions apply until ${nextDateString}. You can book this job in ${state.RestrictionExpiresIn30Days} days.`,
						};
						// set snackbar message
						ui.ProblemMsg = `There is a quote job type and area restriction in place.\nPlease fail the booking as 'No Coverage'.`;
					}
				}
			}

			// return the booking is restricted flag
			return true;
		}
		// otherwise, there is no booking restriction
		// set snackbar message
		if (!token.isCancelled) {
			// set the ui to the coverage message
			ui.JobCoverage = {
				msg: "We have coverage for this area.",
				variant: "success",
			};

			// set the ui to the snackbar message
			ui.ProblemMsg = "We have coverage for this area.";
			ui.ProblemMsgVariant = "success";
			// ui.ProblemShowMsg = snackbarMsg.showMsg;
		}
		// return false indicating no booking restriction
		if (!token.isCancelled && callbackFn) callbackFn(true);
		return false;
	};
	// ### END: LookupBookingRestriction

	// LookupResponseTime:
	// --------------------------------------------------------------------------
	// Looks up the response time for the address
	// Returns true if the response time is in the window
	// --------------------------------------------------------------------------
	public static lookupResponseTime: LookupResponseTime = async ({
		ui,
		token,
		callbackFn,
	}) => {
		// make empty message
		let responseMessage = "";

		// get the postcode area
		const postCodeArea = state.JobPostcodeArea;

		// get the response times from cache
		const responseTimes =
			cacheUtils.getCache<CacheTypes.ResponseTimes>("ResponseTimes");

		// get the response time for the postcode area
		const responseTime = responseTimes?.find(
			(x) => x.postCodeArea === postCodeArea,
		);

		// if the response time is found, set the response time
		if (responseTime) {
			// use switch to set the response message
			switch (
				responseTime[
					`${state.JobTradeCode.toLowerCase()}` as keyof CacheTypes.ResponseTimes
				]
			) {
				case 0:
					responseMessage = "";
					if (!token.isCancelled) {
						ui.JobCoverage = { msg: responseMessage, variant: "success" };
						ui.ProblemMsgVariant = "success";
					}
					if (!token.isCancelled && callbackFn) callbackFn(true);
					return true;
				case 1:
					responseMessage = "Rightio Engineer Area - Fast Response";
					if (!token.isCancelled) {
						ui.JobCoverage = { msg: responseMessage, variant: "success" };
						ui.ProblemMsg = responseMessage;
						ui.ProblemMsgVariant = "success";
					}
					if (!token.isCancelled && callbackFn) callbackFn(true);
					return true;
				case 2:
					responseMessage = "";
					if (!token.isCancelled) {
						ui.JobCoverage = { msg: responseMessage, variant: "success" };
						ui.ProblemMsgVariant = "success";
					}
					if (!token.isCancelled && callbackFn) callbackFn(true);
					return true;
				case 3:
					responseMessage = "NO AVAILABILITY - DO NOT BOOK";
					if (!token.isCancelled) {
						ui.JobCoverage = { msg: responseMessage, variant: "error" };
						ui.ProblemMsg = responseMessage;
						ui.ProblemMsgVariant = "error";
					}
					if (!token.isCancelled && callbackFn) callbackFn(false);
					return false;
				default:
					responseMessage = "";
					if (!token.isCancelled && callbackFn) callbackFn(true);
					return true;
			}
		}
		if (!token.isCancelled && callbackFn) callbackFn(false);
		return false;
	};
	// ### END: LookupResponseTime

	// CheckCoverage:
	// --------------------------------------------------------------------------
	// Checks the coverage for the address
	// Returns the coverage as a boolean
	// --------------------------------------------------------------------------
	public static checkCoverage: CheckCoverage = async ({
		ui,
		token,
		api,
		callbackFn,
	}) => {
		// set global coverage flag
		// globals.noCoverage = false;

		// get the booking restriction from cache
		const bookingRestriction = await this.lookupBookingRestriction({
			ui,
			token,
			callbackFn,
		});

		// get lastJobs
		await api.fetchLast2({
			postcode: state.JobPostcode,
			property: state.JobProperty,
		});

		// if restriction is set, return false
		if (bookingRestriction) {
			// globals.noCoverage = true;
			if (!token.isCancelled) {
				ui.ProblemShowMsg = true;
			}
			if (!token.isCancelled && callbackFn) callbackFn(false);
			return false;
		}

		// get the response time
		const responseTime = await this.lookupResponseTime({
			ui,
			token,
			callbackFn,
		});

		// if no response time is set, return false
		if (!responseTime) {
			// globals.noCoverage = true;
			if (!token.isCancelled) {
				ui.ProblemShowMsg = true;
			}
			if (!token.isCancelled && callbackFn) callbackFn(false);
			return false;
		}
		if (!token.isCancelled) {
			ui.ProblemShowMsg = true;
		}
		if (!token.isCancelled && callbackFn) callbackFn(true);
		return true;
	};
	// ### END: CheckCoverage

	// ClearPropertyAddressForm:
	// --------------------------------------------------------------------------
	// Clears the address fields for property address form
	// --------------------------------------------------------------------------
	public static clearPropertyAddressForm: ClearPropertyAddressForm = () => {
		// create export function to clear all property address fields for UI only if they have been set
		dispatchSetMultipleUI({
			PropertyAddressProperty: "",
			PropertyAddressStreet: "",
			PropertyAddressPostcode: "",
			PropertyAddressCity: "",
			PropertyAddressLocality: "",
			PropertyAddressBusinessName: "",
			PropertyAddressIsBusiness: false,
		});
	};
	// ### END: ClearPropertyAddressForm

	// // AddressToAddressString:
	// // --------------------------------------------------------------------------
	// // Converts the address to address string, adds the county
	// // --------------------------------------------------------------------------
	// public static addressToAddressString: AddressToAddressString = ({ address, mappedCounty }) => {
	//   // if address string is set, return it
	//   if (address?.address_String) return address.address_String;

	//   // identify the case
	//   // has sub-building name only
	//   // has building number has sub-building name only
	//   // has building name has building number has sub-building name only
	//   // has building name only
	//   // has building name has building number only
	//   // has building number only
	//   // has building name has sub-building name only
	//   // order is always sub-building name, building name, building number
	//   const getFirstPartOfAddress = (address: AddressFromAPI) => {
	//     if (address?.sub_Building_Name && address?.building_Name && !address?.building_Number) {
	//       return `${address.sub_Building_Name}, ${address.building_Name}, `;
	//       // has building name has sub-building name only ex. Property 2, Finlay Court, Commonwealth Drive, CRAWLEY, West Sussex, RH10 1AJ
	//     }
	//     if (address?.sub_Building_Name && address?.building_Name && address?.building_Number) {
	//       if (address?.organisation_Name) {
	//         return `${address.sub_Building_Name}, ${address.building_Name}, ${address.building_Number} `;
	//         // Flexibase, West Wing, Trigate, 210-222 Hagley Road West, OLDBURY, West Midlands, B68 0NP
	//         // ex. Deutsch @ Deutsch Ltd, Building A, Oriental Carpet Centre, 105 Eade Road, Haringey, LONDON, London, N4 1TJ
	//         // B4 at the end is tricky
	//         // Eurosat Midlands Ltd, Unit 1, Green Court B4, 4 William Way, Moss Industrial Estate, LEIGH, Lancashire, WN7 3QA
	//       } else {
	//         return `${address.sub_Building_Name}, ${address.building_Name}, ${address.building_Number} `;
	//       }
	//       // has building name has building number has sub-building name only ex. Property 53, Taona House, 1 Merrion Avenue, STANMORE, HA7 4BS
	//     }
	//     if (address?.building_Name && !address?.building_Number && !address?.sub_Building_Name) {
	//       return `${address.building_Name} `;
	//       // has building name only ex. 146b Pottery Road, OLDBURY, West Midlands, B68 9HD
	//     }
	//     if (address?.building_Name && address?.building_Number && !address?.sub_Building_Name) {
	//       return `${address.building_Name}, ${address.building_Number} `;
	//       // has building name has building number only (no sub-building name) # Business Address Most Likely ex. Rightio, Trigate, 210-222 Hagley Road West, OLDBURY, West Midlands, B68 0NP
	//       // Nick Dunn Enterprises, The Old School, 346 Loughborough Road, LEICESTER, Leicestershire, LE4 5PJ
	//     }
	//     if (address?.building_Number && !address?.building_Name && address?.sub_Building_Name) {
	//       return `${address.sub_Building_Name}, ${address.building_Number} `;
	//       // has building number has sub-building name only ex. Property 12, 3 Twig Folly Close, Tower Hamlets, LONDON, London, E2 0SU
	//       // business ex. Bord Na Gaidhlig, Unit 3, 22 Mansfield Street, GLASGOW, Lanarkshire, G11 5QP
	//     }
	//     if (address?.building_Number && !address?.building_Name && !address?.building_Name) {
	//       return `${address.building_Number} `;
	//       // has building number only ex. 164 Pottery Road, OLDBURY, West Midlands, B68 9HD
	//       // business St. Margarets Hospice Shop, 20 Cowgate, Kirkintilloch, GLASGOW, Lanarkshire, G66 1HN
	//     }
	//     if (address?.sub_Building_Name && !address?.building_Name && !address?.building_Number) {
	//       return `${address.sub_Building_Name}`; // has sub-building name only // this appears to have no cases test at the end
	//     }
	//     return "";
	//   };

	//   // get mappedCounty if not existing
	//   mappedCounty = mappedCounty
	//     ? mappedCounty
	//     : postcodeToCountyMapper(this.getPostcodeDistrict({ postcode: address.postcode || "" }) || "");
	//   const addressStringArr: string[] = [];

	//   // parts always separated by comma
	//   address.street && addressStringArr.push(address.street);
	//   address.locality && addressStringArr.push(address.locality);
	//   address.city && addressStringArr.push(address.city);
	//   address.county && addressStringArr.push(address.county);
	//   address.postcode && addressStringArr.push(address.postcode);

	//   const addressString =
	//     (address.organisation_Name ? address.organisation_Name + ", " : "") +
	//     getFirstPartOfAddress(address) +
	//     addressStringArr.join(", ");
	//   // const addressString = `${address.organisation_Name ? `${address.organisation_Name},` : ""}${getFirstPartOfAddress(address)} ${address.street},${address.locality ? `${address.locality},` : ""} ${address.city},${
	//   //   mappedCounty ? `${mappedCounty},` : ""
	//   // } ${address.postcode}`;

	//   return addressString;
	// };
	// // ### END: AddressToAddressString

	// ProcessAddressesFromDB:
	// --------------------------------------------------------------------------
	// Processes the addresses from the DB
	// --------------------------------------------------------------------------
	public static processAddressesFromDB: ProcessAddressesFromDB = ({
		addresses,
		scope,
	}) => {
		let addressResources: AddressFromAPI[] = [];
		if (addresses.length > 0 && addresses[0].postcode) {
			const postcodeDistrict = this.getPostcodeValues({
				postcode: addresses[0].postcode,
				scope,
			})?.district;
			if (postcodeDistrict) {
				addressResources = addresses.map((address) => ({
					...address,
					scope: scope as Scope,
				}));
			}
		}
		return addressResources;
	};
	// ### END: ProcessAddressesFromDB

	// // ProcessAddressesSample:
	// // --------------------------------------------------------------------------
	// // Processes the addresses from sample
	// // --------------------------------------------------------------------------
	// public static processAddressesFromSample = () => {
	//   const addresses: AddressFromAPI[] = [];
	//   addresses.push(test.addressForm() as AddressFromAPI);

	//   let addressResources: AddressFromAPI[] = [];
	//   if (addresses.length > 0) {
	//     const postcodeDistrict = this.getPostcodeValues({ postcode: addresses[0].postcode || "" })?.district;
	//     if (postcodeDistrict) {
	//       // create mappedCounty string
	//       const mappedCounty = postcodeToCountyMapper(postcodeDistrict);

	//       addressResources = addresses.map((address) => ({
	//         ...address,
	//         scope: "Test",
	//         addressString: this.addressToAddressString({
	//           mappedCounty,
	//           address,
	//         }),
	//         county: mappedCounty,
	//       })) as AddressFromAPI[];
	//     }
	//   }
	//   return addressResources;
	// };
	// // ### END: ProcessAddressesFromDB

	// MapAddressResourceToFormAddress:
	// --------------------------------------------------------------------------
	// Maps the address to the form address
	// --------------------------------------------------------------------------
	public static mapAddressResourceToFormAddress: MapAddressToFormAddress = (
		addressResource,
	) => {
		const {
			organisationName: BusinessName,
			street: Street,
			locality: Locality,
			addressObj: { premise: Property } = {
				premise: "",
			}, // Provide a default value to handle cases where addressObj might be undefined
			city: City,
			county: County,
			postcode: Postcode,
		} = addressResource || {};

		const fields: AddressFormFields = {
			Property,
			Street,
			City,
			Locality,
			County,
			Postcode,
			BusinessName,
		};
		const formAddress: AddressFormFields = Object.keys(fields).reduce(
			(acc, key) => {
				if (fields[key as keyof AddressFormFields]) {
					acc[key as keyof AddressFormFields] =
						fields[key as keyof AddressFormFields];
				} else {
					acc[key as keyof AddressFormFields] = "";
				}
				return acc;
			},
			{} as AddressFormFields,
		);
		return formAddress;
	};
	// ### END: MapAddressResourceToFormAddress

	// GetProblemInformationAddressString:
	// --------------------------------------------------------------------------
	// Returns the problem information address string
	// --------------------------------------------------------------------------
	public static getProblemInformationAddressString: GetProblemInformationAddressString =
		async () => {
			const addresses =
				await StorageUtils.getAllValuesFromStoreInIndexedDB<AddressFromAPI>({
					tableName: "PropertyAddresses",
				});

			return addresses && addresses.length > 0
				? `${addresses[0].addressString?.split(", ").slice(-3, -1).join(", ")}`
				: null;
		};

	// Create ADDRESSES table in IndexedDB
	// --------------------------------------------------------------------------
	// Creates the ADDRESSES table in IndexedDB
	// --------------------------------------------------------------------------
	public static createAddressTableInIndexedDB = async () => {
		await StorageUtils.openIndexedDB();
		// await db.addDbInfo({ name, version: 1, description: "This database is used as temporary store for fetched address from PAF database", created: new Date(), modified: new Date() });
	};

	public static convertToClientScope: ConvertToClientScope = (scope) => {
		switch (scope) {
			case "Reload":
				return "PropertyAddress";
			case "ServiceAgreementCustomer":
				return "CarePlanCustomerAddress";
			case "ServiceAgreementPolicy":
				return "CarePlanPlanDetailsAddress";
			case "ServiceAgreementBilling":
				return "CarePlanBillingAddress";
			case "BookingPayment":
				return "BookingPaymentAddress";
			default:
				throw new Error("Invalid scope");
		}
	};

	public static convertToIndexedDBScope: ConvertToIndexedDBScope = (scope) => {
		switch (scope) {
			case "PropertyAddress":
				return "Property";
			case "CarePlanCustomerAddress":
				return "ServiceAgreementCustomer";
			case "CarePlanPlanDetailsAddress":
				return "ServiceAgreementPolicy";
			case "CarePlanBillingAddress":
				return "ServiceAgreementBilling";
			case "BookingPaymentAddress":
				return "BookingPayment";
			default:
				return scope;
		}
	};

	private static findMatchingAddressFromDropdowns(
		ui: TemplateUIObject,
		scope: AddressPrefix,
	) {
		const propertyAddressDropdown = ui.PropertyAddressSelectedAddress;
		const carePlanPlanDetailsDropdown =
			ui.CarePlanPlanDetailsAddressSelectedAddress;
		const carePlanMembershipDropdown =
			ui.CarePlanCustomerAddressSelectedAddress;
		const carePlanBillingDropdown = ui.CarePlanBillingAddressSelectedAddress;
		const allDropdowns = [
			propertyAddressDropdown,
			carePlanPlanDetailsDropdown,
			carePlanMembershipDropdown,
			carePlanBillingDropdown,
		];
		const matchingAddress = allDropdowns.find(
			(a) =>
				a?.id &&
				a.id > 0 &&
				a?.addressObj?.premise === ui[`${scope}Property`] &&
				a?.addressObj?.postcode === ui[`${scope}Postcode`] &&
				a?.street === ui[`${scope}Street`] &&
				(ui[`${scope}BusinessName`]
					? a?.organisationName === ui[`${scope}BusinessName`]
					: true),
		);
		return matchingAddress;
	}
	// --------------------------------------------------------------------------
	// Private methods
	// --------------------------------------------------------------------------
}

declare global {
	interface Window {
		AddressUtils: AddressUtils;
	}
}

window.AddressUtils = AddressUtils;

export default AddressUtils;
